import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Col,
  Dropdown,
  Empty,
  Input,
  MenuProps,
  Modal,
  Row,
  Select,
  Segmented,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EyeOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDate } from '../utils/dateUtils';
import { formatUnit } from '../utils/units';
import { getCatalogProductDimensionParts } from '../utils/productDimensions';
import { isRealProductVariant } from '../utils/productSelectorLabel';
import { Product } from '../types/pz';
import {
  deleteProduct,
  fetchProducts as fetchProductsApi,
  permanentlyDeleteProduct
} from '../modules/products/api';
import {
  calculatePalletSummary,
  calculatePieceMetrics,
  getProductDataGaps,
} from '../modules/products/productLogistics';
import { formatReadout, formatUnitZloty } from '../modules/products/card/cardFormat';
import {
  canDeleteProductCatalogItem,
  canManageProductCatalog,
} from '../modules/products/productCatalogAccessPolicy';
import { ProductCategory, fetchProductCategoryTags } from '../modules/products/productCategoriesApi';
import ProductCategoriesTab from '../components/products/ProductCategoriesTab';
import '../components/layout/AppPage.css';
import './Products.css';

const { Option } = Select;
const { Text } = Typography;

interface ProductRecord extends Product {
  variantCount?: number | string;
}

interface ProductFilters {
  search: string;
  status: 'ACTIVE' | 'INACTIVE' | null;
  productCategoryId: string | null;
}

type CompletenessFilter = 'ALL' | 'INCOMPLETE';

interface ProductCategoryCellProps {
  name: string | null;
  color?: string | null;
  emptyLabel: React.ReactNode;
}

export const ProductCategoryCell: React.FC<ProductCategoryCellProps> = ({
  name,
  color,
  emptyLabel,
}) => {
  if (!name) return <Text type="secondary">{emptyLabel}</Text>;

  return (
    <Tooltip
      title={name}
      placement="topLeft"
      mouseEnterDelay={0.4}
      trigger={['hover', 'focus']}
    >
      <Tag
        className="products-category-tag"
        color={color || 'default'}
        tabIndex={0}
      >
        <span>{name}</span>
      </Tag>
    </Tooltip>
  );
};

const DEFAULT_PRODUCT_FILTERS: ProductFilters = {
  search: '',
  status: 'ACTIVE',
  productCategoryId: null,
};

const isProductActive = (product: ProductRecord) => product.isActive !== false;

export const getRealProductVariants = (product: ProductRecord) => (
  (product.variants || []).filter(isRealProductVariant)
);

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common']);
  const feedback = useFeedback();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'categories' ? 'categories' : 'list';

  const [products, setProducts] = useState<ProductRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<ProductFilters>(DEFAULT_PRODUCT_FILTERS);
  const [categoryTags, setCategoryTags] = useState<ProductCategory[]>([]);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [completeness, setCompleteness] = useState<CompletenessFilter>('ALL');
  const [pendingProductAction, setPendingProductAction] = useState<{
    product: ProductRecord;
    action: 'DEACTIVATE' | 'PERMANENT_DELETE';
  } | null>(null);
  const [productActionLoading, setProductActionLoading] = useState(false);

  const handleTabChange = (key: string) => {
    setSearchParams(key === 'categories' ? { tab: 'categories' } : {});
  };

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageProductCatalog(currentUser);
  const canDelete = canDeleteProductCatalogItem(currentUser);
  const showCost = authService.canViewProductAverageCost();

  const activeAdvancedFilterCount =
    (filters.status !== DEFAULT_PRODUCT_FILTERS.status ? 1 : 0) +
    (filters.productCategoryId ? 1 : 0);
  const hasAnyFilter =
    filters.search !== DEFAULT_PRODUCT_FILTERS.search || activeAdvancedFilterCount > 0 || completeness !== 'ALL';

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.productCategoryId]);

  useEffect(() => {
    loadCategoryTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const productData = await fetchProductsApi(filters);
      setProducts(productData);
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryTags = async () => {
    try {
      setCategoryTags(await fetchProductCategoryTags());
    } catch (error: any) {
      // Polish-first: show the translated message, not the backend English string.
      feedback.error(t('products:productCategory.loadFailed'));
    }
  };

  const handleProductCategoriesChanged = async () => {
    await Promise.all([loadCategoryTags(), fetchProducts()]);
  };

  const handleConfirmProductAction = async () => {
    if (!pendingProductAction) return;

    const productName = pendingProductAction.product.name;
    setProductActionLoading(true);
    try {
      if (pendingProductAction.action === 'PERMANENT_DELETE') {
        await permanentlyDeleteProduct(pendingProductAction.product.id);
        feedback.success(t('products:success.deleted', { name: productName }));
      } else {
        await deleteProduct(pendingProductAction.product.id);
        feedback.success(t('products:success.deactivated', { name: productName }));
      }
      setPendingProductAction(null);
      await fetchProducts();
    } catch (error: any) {
      if (error.response?.data?.code === 'PRODUCT_IN_USE') {
        feedback.error(t('products:error.productInUse', { name: productName }), { sticky: true });
      } else if (pendingProductAction.action === 'PERMANENT_DELETE') {
        feedback.error(t('products:error.deleteFailed', { name: productName }));
      } else {
        feedback.error(t('products:error.deactivateFailed', { name: productName }));
      }
    } finally {
      setProductActionLoading(false);
    }
  };

  const getProductActionItems = (record: ProductRecord): MenuProps['items'] => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: t('common:view'),
    },
    ...(canDelete ? [{
      key: isProductActive(record) ? 'deactivate' : 'permanent-delete',
      danger: true,
      icon: <DeleteOutlined />,
      label: isProductActive(record)
        ? t('products:actions.deactivate')
        : t('products:actions.permanentDelete'),
    }] : []),
  ];

  const handleProductAction = (key: string, record: ProductRecord) => {
    if (key === 'view') {
      navigate(`/app/products/${record.id}`);
      return;
    }

    if (key === 'deactivate' || key === 'permanent-delete') {
      setPendingProductAction({
        product: record,
        action: key === 'permanent-delete' ? 'PERMANENT_DELETE' : 'DEACTIVATE',
      });
    }
  };

  const columns: ColumnsType<ProductRecord> = [
    {
      title: t('common:actions'),
      key: 'actions',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getProductActionItems(record),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              handleProductAction(key, record);
            },
          }}
          trigger={['click']}
        >
          <Button
            className="products-actions-button"
            icon={<MoreOutlined />}
            size="small"
            onClick={(event) => event.stopPropagation()}
            title={t('common:actions')}
          />
        </Dropdown>
      ),
    },
    {
      title: t('products:list.columns.product'),
      dataIndex: 'name',
      key: 'name',
      width: 280,
      render: (name: string, record) => {
        const piece = calculatePieceMetrics(record);
        return (
          <div className="products-identity">
            <button className="products-name-button" onClick={() => navigate(`/app/products/${record.id}`)}>
              <span>{name}</span>
            </button>
            <ProductCategoryCell
              name={record.productCategoryName ?? null}
              color={record.productCategoryColor}
              emptyLabel={t('products:productCategory.none')}
            />
            {piece.areaM2 && (
              <small className="products-identity__meta">
                {t('products:list.areaPerPiece', { value: formatReadout(piece.areaM2, 3) })}
              </small>
            )}
          </div>
        );
      },
    },
    {
      title: t('products:list.columns.piece'),
      key: 'dimensions',
      width: 118,
      render: (_, record) => {
        const parts = getCatalogProductDimensionParts(record);
        if (parts.length === 0) return <span className="products-muted">—</span>;

        return (
          <dl className="products-dimensions">
            {parts.map((part) => (
              <div className="products-dimension" key={part.field}>
                <dt>{part.label}</dt>
                <dd>
                  <strong>{part.value}</strong>
                  <span className="products-dimensions-unit">mm</span>
                </dd>
              </div>
            ))}
          </dl>
        );
      },
    },
    {
      title: t('products:list.columns.pallet'),
      key: 'pallet',
      width: 140,
      render: (_, record) => {
        const pallet = calculatePalletSummary(record);
        if (!pallet.pieces || !pallet.ldm) {
          return <span className="products-missing">{t('products:list.noPallet')}</span>;
        }
        const main = [
          t('products:list.palletPieces', { value: formatReadout(pallet.pieces, 3) }),
          pallet.areaM2 ? t('products:list.palletArea', { value: formatReadout(pallet.areaM2, 1) }) : null,
        ].filter(Boolean).join(' · ');
        const sub = [
          t('products:list.palletLdm', { value: formatReadout(pallet.ldm, 2) }),
          pallet.packages ? t('products:list.palletPackages', { value: formatReadout(pallet.packages, 0) }) : null,
        ].filter(Boolean).join(' · ');
        return (
          <div className="products-stack">
            <span className="products-num">{main}</span>
            <small className="products-identity__meta">{sub}</small>
          </div>
        );
      },
    },
    {
      title: t('products:list.columns.weight'),
      key: 'weight',
      width: 104,
      render: (_, record) => (Number(record.grossWeight) > 0
        ? <span className="products-num">{t('products:list.kgPerPiece', { value: formatReadout(Number(record.grossWeight), 3) })}</span>
        : <span className="products-missing">{t('products:list.noWeight')}</span>),
    },
    ...(showCost ? [{
      title: t('products:list.columns.cost'),
      key: 'averageCost',
      width: 140,
      render: (_: unknown, record: ProductRecord) => {
        if (record.averagePurchasePricePLN === null || record.averagePurchasePricePLN === undefined) {
          return <span className="products-missing">{t('products:list.noCost')}</span>;
        }
        const who = [
          record.averagePurchasePriceUpdatedByName,
          record.averagePurchasePriceUpdatedAt ? formatDate(record.averagePurchasePriceUpdatedAt) : null,
        ].filter(Boolean).join(' · ');
        return (
          <div className="products-stack">
            <span className="products-num">
              {t('products:card.cost.perUnit', {
                value: formatUnitZloty(Number(record.averagePurchasePricePLN)),
                unit: formatUnit(record.averagePurchasePriceUnit || 'pcs'),
              })}
            </span>
            {who && <small className="products-identity__meta">{who}</small>}
          </div>
        );
      },
    }] : []),
    {
      title: t('products:list.columns.variants'),
      key: 'variants',
      width: 84,
      render: (_, record) => {
        const count = getRealProductVariants(record).filter((variant) => variant.isActive !== false).length;
        return count > 0 ? <span className="products-num">{count}</span> : <span className="products-muted">—</span>;
      },
    },
    {
      title: t('products:list.columns.data'),
      key: 'data',
      width: 140,
      render: (_, record) => {
        const gaps = getProductDataGaps(record, { includeCost: showCost });
        return gaps.length === 0
          ? <span className="products-complete">{t('products:list.complete')}</span>
          : (
            <small className="products-identity__meta">
              {t('products:list.missing', { items: gaps.map((gap) => t(`products:card.gaps.${gap}`)).join(', ') })}
            </small>
          );
      },
    },
  ];

  const incompleteProducts = products.filter((product) => (
    getProductDataGaps(product, { includeCost: showCost }).length > 0
  ));
  const visibleProducts = completeness === 'INCOMPLETE' ? incompleteProducts : products;

  return (
    <div className="app-page products-page">
      <Tabs
        className="app-page__tabs"
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'list',
            label: t('products:title'),
            children: (
              <section className="app-page__card">
      <div className="app-page__toolbar">
        <Input
          className="app-page__toolbar-search"
          placeholder={t('products:filters.search')}
          prefix={<SearchOutlined />}
          value={filters.search}
          onChange={(event) => setFilters({ ...filters, search: event.target.value })}
          allowClear
        />
        <div className="app-page__toolbar-actions">
          <div className="app-page__toolbar-group">
            <Segmented
              aria-label={t('products:list.filterLabel')}
              value={completeness}
              onChange={(value) => setCompleteness(value as CompletenessFilter)}
              options={[
                { label: <span>{t('products:list.all')} <span className="products-count">{products.length}</span></span>, value: 'ALL' },
                { label: <span>{t('products:list.incomplete')} <span className="products-count">{incompleteProducts.length}</span></span>, value: 'INCOMPLETE' },
              ]}
            />
          </div>
          <Badge count={activeAdvancedFilterCount} size="small" offset={[-2, 2]}>
            <Button
              icon={<FilterOutlined />}
              type={advancedFiltersOpen || activeAdvancedFilterCount > 0 ? 'primary' : 'default'}
              ghost={advancedFiltersOpen || activeAdvancedFilterCount > 0}
              onClick={() => setAdvancedFiltersOpen((open) => !open)}
            >
              {t('products:filters.toggle')}
            </Button>
          </Badge>
          {hasAnyFilter && (
            <Button
              type="link"
              onClick={() => { setFilters(DEFAULT_PRODUCT_FILTERS); setCompleteness('ALL'); }}
            >
              {t('common:clearFilters')}
            </Button>
          )}
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/app/products/new')}
            >
              {t('products:actions.create')}
            </Button>
          )}
        </div>
      </div>

      {advancedFiltersOpen && (
        <Row gutter={[12, 12]} className="app-page__filters">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              aria-label={t('products:filters.status')}
              placeholder={t('products:filters.status')}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              style={{ width: '100%' }}
            >
              <Option value="ACTIVE">{t('products:status.active')}</Option>
              <Option value="INACTIVE">{t('products:status.inactive')}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder={t('products:filters.category')}
              value={filters.productCategoryId}
              onChange={(value) => setFilters({ ...filters, productCategoryId: value ?? null })}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              options={categoryTags.map((tag) => ({ value: tag.id, label: tag.name }))}
            />
          </Col>
        </Row>
      )}

      <Table
        className="products-table"
        dataSource={visibleProducts}
        columns={columns}
        loading={loading}
        rowKey="id"
        size="middle"
        tableLayout="fixed"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={completeness === 'INCOMPLETE' ? t('products:list.emptyFilter') : t('products:empty')}
            />
          ),
        }}
        scroll={{ x: 1040 }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total, range) => t('common:pagination', { from: range[0], to: range[1], total }),
          pageSizeOptions: ['10', '25', '50', '100'],
          defaultPageSize: 25,
        }}
      />
              </section>
            ),
          },
          {
            key: 'categories',
            label: t('products:productCategory.tab'),
            children: (
              <section className="app-page__card">
                <ProductCategoriesTab canEdit={canEdit} onCategoriesChanged={handleProductCategoriesChanged} />
              </section>
            ),
          },
        ]}
      />
      <Modal
        title={pendingProductAction?.action === 'PERMANENT_DELETE'
          ? t('products:confirm.permanentDelete')
          : t('products:confirm.deactivate')}
        open={Boolean(pendingProductAction)}
        okText={pendingProductAction?.action === 'PERMANENT_DELETE'
          ? t('products:actions.permanentDelete')
          : t('products:actions.deactivate')}
        cancelText={t('common:no')}
        okButtonProps={{ danger: true }}
        confirmLoading={productActionLoading}
        onOk={handleConfirmProductAction}
        onCancel={() => {
          if (!productActionLoading) setPendingProductAction(null);
        }}
      />
    </div>
  );
};

export default Products;
